import { useEffect, useMemo, useState, type ReactNode } from "react"
import { MessageSquareQuote, Plus, RefreshCw, Save, Star, Store, X } from "lucide-react"
import { useDispatch, useSelector } from "react-redux"
import toast from "react-hot-toast"
import StorefrontStoreIdCard from "../../components/Storefront/StorefrontStoreIdCard"
import { syncGoogleStoreReviews, updateStore } from "../../api/products/post"
import randomId from "../../common/randomId"
import { userData } from "../../store/actions"
import { getStoreProductRules } from "../../utils/storeCategory"
import {
  normalizeServiceReviewItems,
  normalizeServiceReviewSources,
  type ServiceReviewItem,
  type ServiceReviewSource,
} from "../../utils/serviceStorefront"

type ReviewStatus = "PENDING" | "APPROVED" | "REJECTED"

const emptyManualReview = {
  author: "",
  role: "",
  rating: 5,
  comment: "",
  reviewUrl: "",
  publishedAt: "",
}

export default function ServiceReviewsPage() {
  const dispatch = useDispatch()
  const userdata = useSelector((state: any) => state.UserReducer.userdata)
  const store = userdata?.store || {}
  const storeId = String(store?._id || store?.id || "")
  const { isServiceStore } = getStoreProductRules(store)

  const [reviewSources, setReviewSources] = useState<ServiceReviewSource[]>([])
  const [reviewItems, setReviewItems] = useState<ServiceReviewItem[]>([])
  const [saving, setSaving] = useState(false)
  const [statusSavingId, setStatusSavingId] = useState("")
  const [syncingGoogle, setSyncingGoogle] = useState(false)
  const [error, setError] = useState("")
  const [showManualModal, setShowManualModal] = useState(false)
  const [manualReview, setManualReview] = useState(emptyManualReview)

  useEffect(() => {
    setReviewSources(normalizeServiceReviewSources(store?.reviewSources))
    setReviewItems(normalizeServiceReviewItems(store?.reviewItems))
    setError("")
  }, [store?.reviewItems, store?.reviewSources])

  const websiteReviews = useMemo(
    () =>
      reviewItems
        .filter((item) => item.channel === "WEBSITE")
        .sort((a, b) => {
          const statusOrder = { PENDING: 0, APPROVED: 1, REJECTED: 2 }
          const byStatus = statusOrder[a.status] - statusOrder[b.status]
          if (byStatus !== 0) return byStatus
          return String(b.publishedAt || "").localeCompare(String(a.publishedAt || ""))
        }),
    [reviewItems]
  )

  const googleReviews = useMemo(
    () =>
      reviewItems
        .filter((item) => item.channel === "GOOGLE")
        .sort((a, b) => String(b.publishedAt || "").localeCompare(String(a.publishedAt || ""))),
    [reviewItems]
  )

  const reviewCounts = useMemo(
    () => ({
      website: websiteReviews.length,
      pendingWebsite: websiteReviews.filter((item) => item.status === "PENDING").length,
      google: googleReviews.length,
    }),
    [googleReviews, websiteReviews]
  )

  const googleSource = useMemo(
    () => reviewSources.find((source) => source.channel === "GOOGLE") || null,
    [reviewSources]
  )

  const updateSource = (
    index: number,
    field: keyof ServiceReviewSource,
    value: string | boolean
  ) => {
    setReviewSources((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    )
  }

  const serializeReviewSources = (items: ServiceReviewSource[]) =>
    items.map((source) => ({
      ...source,
      label: source.label.trim(),
      sourceUrl: source.sourceUrl.trim(),
      placeId: source.placeId.trim(),
      notes: source.notes.trim(),
    }))

  const serializeReviewItems = (items: ServiceReviewItem[]) =>
    items.map((item) => ({
      ...item,
      author: item.author.trim(),
      role: item.role.trim(),
      comment: item.comment.trim(),
      reviewUrl: item.reviewUrl.trim(),
      publishedAt: item.publishedAt.trim(),
      rating: Math.max(1, Math.min(5, Number(item.rating) || 5)),
      status:
        item.status === "APPROVED"
          ? "APPROVED"
          : item.status === "REJECTED"
            ? "REJECTED"
            : "PENDING",
    }))

  const persistReviews = async (nextItems: ServiceReviewItem[], options?: { silent?: boolean }) => {
    if (!storeId) return

    await updateStore(storeId, {
      reviewSources: serializeReviewSources(reviewSources),
      reviewItems: serializeReviewItems(nextItems),
    })

    dispatch(userData())

    if (!options?.silent) {
      toast.success("Avis enregistres")
    }
  }

  const updateReviewStatus = async (reviewId: string, status: ReviewStatus) => {
    const previousItems = reviewItems
    const nextItems = reviewItems.map((item) =>
      item.id === reviewId ? { ...item, status } : item
    )

    setReviewItems(nextItems)

    try {
      setStatusSavingId(reviewId)
      setError("")
      await persistReviews(nextItems, { silent: true })
      toast.success("Statut de l’avis mis a jour")
    } catch (e: any) {
      setReviewItems(previousItems)
      setError(e?.message || "Impossible d'enregistrer le statut de l'avis")
    } finally {
      setStatusSavingId("")
    }
  }

  const addManualReview = () => {
    const trimmedAuthor = manualReview.author.trim()
    const trimmedComment = manualReview.comment.trim()

    if (!trimmedAuthor || trimmedComment.length < 8) {
      setError("Renseignez au minimum le nom et un commentaire valide pour l’avis manuel.")
      return
    }

    setReviewItems((current) => [
      {
        id: randomId("review_"),
        channel: "WEBSITE",
        author: trimmedAuthor,
        role: manualReview.role.trim(),
        rating: Math.max(1, Math.min(5, Number(manualReview.rating) || 5)),
        comment: trimmedComment,
        reviewUrl: manualReview.reviewUrl.trim(),
        publishedAt: manualReview.publishedAt.trim() || new Date().toISOString().slice(0, 10),
        status: "APPROVED",
      },
      ...current,
    ])

    setManualReview(emptyManualReview)
    setShowManualModal(false)
    setError("")
  }

  const syncGoogleReviews = async () => {
    if (!storeId) return

    try {
      setSyncingGoogle(true)
      setError("")

      const response = await syncGoogleStoreReviews(storeId, {
        reviewSources: serializeReviewSources(reviewSources),
      })

      const nextSources = normalizeServiceReviewSources(response?.data?.reviewSources)
      const nextItems = normalizeServiceReviewItems(response?.data?.reviewItems)

      setReviewSources(nextSources)
      setReviewItems(nextItems)
      toast.success(`${Number(response?.data?.importedCount || 0)} review(s) Google synchronisee(s)`)
    } catch (e: any) {
      setError(e?.message || "Impossible de synchroniser les reviews Google")
    } finally {
      setSyncingGoogle(false)
    }
  }

  const handleSave = async () => {
    if (!storeId) return

    try {
      setSaving(true)
      setError("")
      await persistReviews(reviewItems)
    } catch (e: any) {
      setError(e?.message || "Impossible d'enregistrer les avis")
    } finally {
      setSaving(false)
    }
  }

  if (!isServiceStore) {
    return <ServiceStorefrontUnavailable title="Reviews" />
  }

  return (
    <div className="space-y-6">
      <header className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
              <MessageSquareQuote size={22} />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-700">Storefront</p>
              <h1 className="mt-2 text-2xl font-bold text-slate-900">Reviews</h1>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                Les avis recus depuis le site web sont listes ici en priorite. Le gestionnaire peut les approuver avant affichage sur le site, puis completer la vitrine avec des avis ajoutes manuellement ou importes depuis Google.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setShowManualModal(true)}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <Plus size={16} />
              Ajouter un avis manuel
            </button>
            <button
              type="button"
              onClick={syncGoogleReviews}
              disabled={syncingGoogle || !storeId}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
            >
              <RefreshCw size={16} className={syncingGoogle ? "animate-spin" : ""} />
              {syncingGoogle ? "Synchronisation..." : "Importer reviews Google"}
            </button>
          </div>
        </div>
      </header>

      <StorefrontStoreIdCard store={store} />

      {error ? <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-600">{error}</div> : null}

      <div className="grid gap-4 md:grid-cols-3">
        <KpiCard label="Avis du site" value={String(reviewCounts.website)} hint={`${reviewCounts.pendingWebsite} en attente`} />
        <KpiCard label="Google reviews" value={String(reviewCounts.google)} hint={googleSource?.label || "Source configurable"} />
        <KpiCard label="Moderation" value="Table prioritaire" hint="Approuver avant publication" />
      </div>

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-5">
          <p className="text-lg font-semibold text-slate-900">Avis recus depuis le site web</p>
          <p className="mt-1 text-sm text-slate-500">
            Les visiteurs PNS soumettent leurs avis ici. Approuvez-les pour qu’ils apparaissent sur le site.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-6 py-3">Auteur</th>
                <th className="px-6 py-3">Note</th>
                <th className="px-6 py-3">Commentaire</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Statut</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {websiteReviews.length ? websiteReviews.map((review) => (
                <tr key={review.id} className="border-t border-slate-100 align-top">
                  <td className="px-6 py-4">
                    <p className="font-semibold text-slate-900">{review.author || "-"}</p>
                    <p className="mt-1 text-xs text-slate-500">{review.role || "Visiteur du site"}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1 text-amber-500">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <Star key={index} size={15} fill={index < review.rating ? "currentColor" : "none"} />
                      ))}
                    </div>
                  </td>
                  <td className="max-w-md px-6 py-4 text-slate-700">
                    {review.comment || "-"}
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {review.publishedAt || "-"}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={review.status} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <ActionButton
                        label="Approuver"
                        active={review.status === "APPROVED"}
                        tone="approve"
                        disabled={statusSavingId === review.id}
                        onClick={() => updateReviewStatus(review.id, "APPROVED")}
                      />
                      <ActionButton
                        label="Attente"
                        active={review.status === "PENDING"}
                        tone="pending"
                        disabled={statusSavingId === review.id}
                        onClick={() => updateReviewStatus(review.id, "PENDING")}
                      />
                      <ActionButton
                        label="Rejeter"
                        active={review.status === "REJECTED"}
                        tone="reject"
                        disabled={statusSavingId === review.id}
                        onClick={() => updateReviewStatus(review.id, "REJECTED")}
                      />
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-slate-500">
                    Aucun avis visiteur recu pour le moment.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-lg font-semibold text-slate-900">Source Google</p>
              <p className="mt-1 text-sm text-slate-500">
                Conservez ici le lien de la fiche Google et le Place ID utilises pour la synchronisation automatique.
              </p>
            </div>
          </div>

          <div className="mt-5 space-y-4">
            {reviewSources.map((source, index) => (
              <article key={source.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-400">{source.channel}</p>
                    <p className="mt-1 font-semibold text-slate-900">{source.label}</p>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={source.enabled}
                      onChange={(e) => updateSource(index, "enabled", e.target.checked)}
                    />
                    Actif
                  </label>
                </div>

                <div className="mt-4 space-y-4">
                  <Field label="Libelle">
                    <input
                      value={source.label}
                      onChange={(e) => updateSource(index, "label", e.target.value)}
                      className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-800"
                    />
                  </Field>

                  <Field label={source.channel === "GOOGLE" ? "Lien fiche Google" : "Lien source / formulaire"}>
                    <input
                      value={source.sourceUrl}
                      onChange={(e) => updateSource(index, "sourceUrl", e.target.value)}
                      placeholder="https://..."
                      className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-800"
                    />
                  </Field>

                  {source.channel === "GOOGLE" ? (
                    <Field label="Google Place ID">
                      <input
                        value={source.placeId}
                        onChange={(e) => updateSource(index, "placeId", e.target.value)}
                        placeholder="ChIJ..."
                        className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-800"
                      />
                    </Field>
                  ) : null}

                  <Field label="Notes">
                    <textarea
                      value={source.notes}
                      onChange={(e) => updateSource(index, "notes", e.target.value)}
                      rows={3}
                      className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-800"
                    />
                  </Field>

                  {source.channel === "GOOGLE" ? (
                    <p className="text-xs leading-relaxed text-slate-500">
                      La synchronisation automatique utilise le `Place ID` configure ici et la cle API Google du backend.
                    </p>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-5">
            <p className="text-lg font-semibold text-slate-900">Google reviews importees</p>
            <p className="mt-1 text-sm text-slate-500">
              Cette liste sert d’inventaire des avis Google deja importes et affichables sur le site.
            </p>
          </div>

          <div className="space-y-4 p-6">
            {googleReviews.length ? googleReviews.map((review) => (
              <article key={review.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-slate-900">{review.author || "Google reviewer"}</p>
                    <p className="mt-1 text-xs text-slate-500">{review.publishedAt || "-"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={review.status} />
                    <div className="flex gap-1.5">
                      <ActionButton
                        label="Publier"
                        active={review.status === "APPROVED"}
                        tone="approve"
                        disabled={statusSavingId === review.id}
                        onClick={() => updateReviewStatus(review.id, "APPROVED")}
                      />
                      <ActionButton
                        label="Attente"
                        active={review.status === "PENDING"}
                        tone="pending"
                        disabled={statusSavingId === review.id}
                        onClick={() => updateReviewStatus(review.id, "PENDING")}
                      />
                      <ActionButton
                        label="Masquer"
                        active={review.status === "REJECTED"}
                        tone="reject"
                        disabled={statusSavingId === review.id}
                        onClick={() => updateReviewStatus(review.id, "REJECTED")}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex gap-1 text-amber-500">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Star key={index} size={15} fill={index < review.rating ? "currentColor" : "none"} />
                  ))}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-700">{review.comment || "-"}</p>
              </article>
            )) : (
              <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-sm text-slate-500">
                Aucune review Google importee pour le moment.
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
        >
          <Save size={18} />
          {saving ? "Enregistrement..." : "Enregistrer les avis"}
        </button>
      </div>

      {showManualModal ? (
        <ModalShell title="Ajouter un avis manuel" onClose={() => setShowManualModal(false)}>
          <div className="space-y-4">
            <Field label="Auteur">
              <input
                value={manualReview.author}
                onChange={(e) => setManualReview((current) => ({ ...current, author: e.target.value }))}
                className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800"
              />
            </Field>
            <Field label="Role / contexte">
              <input
                value={manualReview.role}
                onChange={(e) => setManualReview((current) => ({ ...current, role: e.target.value }))}
                className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800"
              />
            </Field>
            <Field label="Note">
              <select
                value={manualReview.rating}
                onChange={(e) => setManualReview((current) => ({ ...current, rating: Number(e.target.value) }))}
                className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800"
              >
                {[5, 4, 3, 2, 1].map((value) => (
                  <option key={value} value={value}>{value}/5</option>
                ))}
              </select>
            </Field>
            <Field label="Commentaire">
              <textarea
                value={manualReview.comment}
                onChange={(e) => setManualReview((current) => ({ ...current, comment: e.target.value }))}
                rows={4}
                className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800"
              />
            </Field>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Lien de l’avis">
                <input
                  value={manualReview.reviewUrl}
                  onChange={(e) => setManualReview((current) => ({ ...current, reviewUrl: e.target.value }))}
                  className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800"
                />
              </Field>
              <Field label="Date">
                <input
                  value={manualReview.publishedAt}
                  onChange={(e) => setManualReview((current) => ({ ...current, publishedAt: e.target.value }))}
                  placeholder="2026-06-25"
                  className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800"
                />
              </Field>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowManualModal(false)}
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={addManualReview}
                className="rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Ajouter
              </button>
            </div>
          </div>
        </ModalShell>
      ) : null}

    </div>
  )
}

function KpiCard({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{hint}</p>
    </div>
  )
}

function StatusBadge({ status }: { status: ReviewStatus }) {
  const tone =
    status === "APPROVED"
      ? "bg-emerald-50 text-emerald-700"
      : status === "REJECTED"
        ? "bg-rose-50 text-rose-700"
        : "bg-amber-50 text-amber-700"

  const label =
    status === "APPROVED" ? "Approuve" : status === "REJECTED" ? "Rejete" : "En attente"

  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ${tone}`}>{label}</span>
}

function ActionButton({
  label,
  active,
  tone,
  disabled = false,
  onClick,
}: {
  label: string
  active: boolean
  tone: "approve" | "pending" | "reject"
  disabled?: boolean
  onClick: () => void
}) {
  const toneClass =
    tone === "approve"
      ? active
        ? "bg-emerald-600 text-white border-emerald-600"
        : "border-emerald-200 text-emerald-700"
      : tone === "reject"
        ? active
          ? "bg-rose-600 text-white border-rose-600"
          : "border-rose-200 text-rose-700"
        : active
          ? "bg-amber-500 text-white border-amber-500"
          : "border-amber-200 text-amber-700"

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-xl border px-3 py-2 text-xs font-semibold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 ${toneClass}`}
    >
      {label}
    </button>
  )
}

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string
  children: ReactNode
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
          <p className="text-lg font-semibold text-slate-900">{title}</p>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  )
}

function ServiceStorefrontUnavailable({ title }: { title: string }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
          <Store size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          <p className="mt-2 text-sm text-slate-600">
            Cette page est reservee aux boutiques de type service.
          </p>
        </div>
      </div>
    </div>
  )
}
